/**
 * @file date-time-pickers/shared/calendar-model.ts
 *
 * Pure calendar math functions — no React dependencies.
 * 100% testable, reusable for Date Pickers and Time Pickers.
 *
 * All timestamps are in UTC milliseconds to prevent timezone shift issues.
 */

import type {
	CalendarWeek,
	DateLocale,
	ResolvedDateLocale,
	SelectableDates,
} from "./types";

// ─── UTC Date Helpers ─────────────────────────────────────────────────────────

/**
 * Normalizes a timestamp to UTC midnight (00:00:00.000 UTC).
 * Essential for consistent day comparison across timezones.
 */
export const startOfDayUTC = (ms: number): number => {
	const d = new Date(ms);
	return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

/** Gets UTC midnight of today */
export const todayUTC = (): number => startOfDayUTC(Date.now());

/** Gets UTC midnight of 1st day of month */
export const startOfMonthUTC = (year: number, month: number): number =>
	Date.UTC(year, month, 1);

/** Gets total days in month */
export const getDaysInMonth = (year: number, month: number): number =>
	new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

/** Gets day-of-week (0=Sun, 6=Sat) for 1st day of month */
export const getFirstDayOfWeek = (year: number, month: number): number =>
	new Date(Date.UTC(year, month, 1)).getUTCDay();

// ─── Calendar Grid ────────────────────────────────────────────────────────────

/**
 * Generates calendar grid for a specific month.
 * Returns array of weeks, each week containing 7 cells (null = padding).
 *
 * @param year - Full year
 * @param month - Month (0–11)
 * @param weekStartsOn - 0 = Sunday, 1 = Monday
 */
export const getWeeksInMonth = (
	year: number,
	month: number,
	weekStartsOn: 0 | 1,
): CalendarWeek[] => {
	const todayMs = todayUTC();
	const daysInMonth = getDaysInMonth(year, month);
	const firstDow = getFirstDayOfWeek(year, month); // 0 = Sun

	// Compute start padding cells according to weekStartsOn
	const startPadding = (firstDow - weekStartsOn + 7) % 7;

	// Total cells = padding + actual days
	const totalCells = startPadding + daysInMonth;
	const totalWeeks = Math.ceil(totalCells / 7);

	const weeks: CalendarWeek[] = [];

	for (let w = 0; w < totalWeeks; w++) {
		const week: CalendarWeek = [];
		for (let d = 0; d < 7; d++) {
			const cellIndex = w * 7 + d;
			const dayNumber = cellIndex - startPadding + 1;

			if (dayNumber < 1 || dayNumber > daysInMonth) {
				week.push(null);
			} else {
				const utcMs = Date.UTC(year, month, dayNumber);
				week.push({
					utcMs,
					day: dayNumber,
					month,
					year,
					isCurrentMonth: true,
					isToday: utcMs === todayMs,
				});
			}
		}
		weeks.push(week);
	}

	return weeks;
};

// ─── Date Comparison ──────────────────────────────────────────────────────────

/** Compares if two timestamps share the same UTC date */
export const isSameDay = (a: number, b: number): boolean =>
	startOfDayUTC(a) === startOfDayUTC(b);

/** Checks if ms falls within [startMs, endMs] (inclusive) */
export const isDateInRange = (
	ms: number,
	startMs: number | null,
	endMs: number | null,
): boolean => {
	if (startMs === null || endMs === null) return false;
	const day = startOfDayUTC(ms);
	const start = startOfDayUTC(startMs);
	const end = startOfDayUTC(endMs);
	return day >= start && day <= end;
};

/** Checks if ms is the start date of range */
export const isRangeStart = (ms: number, startMs: number | null): boolean =>
	startMs !== null && isSameDay(ms, startMs);

/** Checks if ms is the end date of range */
export const isRangeEnd = (ms: number, endMs: number | null): boolean =>
	endMs !== null && isSameDay(ms, endMs);

/** Checks if year is selectable according to `SelectableDates` config */
export const isYearSelectable = (
	year: number,
	selectableDates: SelectableDates,
): boolean => selectableDates.isSelectableYear(year);

// ─── Locale / Month Normalization ─────────────────────────────────────────────

/** Resolves partial DateLocale config with fallback values */
export const resolveLocale = (locale?: DateLocale): ResolvedDateLocale => ({
	locale:
		locale?.locale ??
		(typeof navigator !== "undefined" ? navigator.language : "en-US"),
	weekStartsOn: locale?.weekStartsOn ?? 1,
});

/** Normalizes any timestamp to UTC midnight on the 1st of that month */
export const normalizeToMonthStart = (ms: number): number => {
	const d = new Date(ms);
	return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
};

// ─── Month Navigation ─────────────────────────────────────────────────────────

/**
 * Navigates to next/previous month.
 * @returns UTC ms of the 1st day of target month
 */
export const navigateMonth = (
	currentMs: number,
	direction: "prev" | "next",
): number => {
	const d = new Date(currentMs);
	const year = d.getUTCFullYear();
	const month = d.getUTCMonth();
	if (direction === "next") {
		return month === 11
			? Date.UTC(year + 1, 0, 1)
			: Date.UTC(year, month + 1, 1);
	}
	return month === 0 ? Date.UTC(year - 1, 11, 1) : Date.UTC(year, month - 1, 1);
};

/** Whether a month can be displayed under the picker year constraints. */
export const isMonthNavigable = (
	year: number,
	yearRange: [number, number],
	selectableDates: SelectableDates,
): boolean =>
	year >= yearRange[0] &&
	year <= yearRange[1] &&
	selectableDates.isSelectableYear(year);

/**
 * Return the adjacent month only when its year is allowed. This keeps imperative
 * hook actions and the header controls subject to exactly the same constraints.
 */
export const getNavigableMonth = (
	currentMs: number,
	direction: "prev" | "next",
	yearRange: [number, number],
	selectableDates: SelectableDates,
): number | null => {
	const targetMs = navigateMonth(currentMs, direction);
	const targetYear = new Date(targetMs).getUTCFullYear();
	return isMonthNavigable(targetYear, yearRange, selectableDates)
		? targetMs
		: null;
};

// ─── Date Formatting ──────────────────────────────────────────────────────────

/**
 * Formats a UTC timestamp into a locale-aware date string.
 *
 * @example

 * ```ts
 * formatDate(Date.UTC(2025, 5, 26), { locale: 'en-US', weekStartsOn: 1 })
 * // → "June 26, 2025"
 * ```
 */
export const formatDate = (
	utcMs: number,
	locale: ResolvedDateLocale,
	options?: Intl.DateTimeFormatOptions,
): string => {
	const defaultOptions: Intl.DateTimeFormatOptions = {
		year: "numeric",
		month: "long",
		day: "numeric",
		timeZone: "UTC",
	};
	return new Intl.DateTimeFormat(locale.locale, {
		...defaultOptions,
		...options,
	}).format(new Date(utcMs));
};

/**
 * Formats month + year for calendar header (e.g., "June 2025").
 */
export const formatMonthYear = (
	utcMs: number,
	locale: ResolvedDateLocale,
): string =>
	new Intl.DateTimeFormat(locale.locale, {
		year: "numeric",
		month: "long",
		timeZone: "UTC",
	}).format(new Date(utcMs));

/**
 * Returns weekday names (narrow) according to locale and weekStartsOn.
 * @returns Array of 7 names, starting from weekStartsOn day.
 *
 * @example
 * ```ts
 * getWeekdayNames({ locale: 'en-US', weekStartsOn: 1 })
 * // → ['M', 'T', 'W', 'T', 'F', 'S', 'S']
 * ```
 */
export const getWeekdayNames = (locale: ResolvedDateLocale): string[] => {
	// Fixed reference week (2025-01-05 is Sunday) to extract day names
	const SUNDAY_MS = Date.UTC(2025, 0, 5);
	const formatter = new Intl.DateTimeFormat(locale.locale, {
		weekday: "narrow",
		timeZone: "UTC",
	});

	const allDays = Array.from({ length: 7 }, (_, i) =>
		formatter.format(new Date(SUNDAY_MS + i * 86_400_000)),
	);

	// Rotate according to weekStartsOn: 0=Sun (no rotate), 1=Mon (rotate 1)
	return [
		...allDays.slice(locale.weekStartsOn),
		...allDays.slice(0, locale.weekStartsOn),
	];
};

// ─── Parse helpers ────────────────────────────────────────────────────────────

/**
 * Parses date string in "MM/DD/YYYY" format into UTC ms.
 * @returns UTC ms if valid, null if invalid
 */
export const parseDateInput = (input: string): number | null => {
	const match = input.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
	if (!match) return null;

	const [, mm, dd, yyyy] = match;
	const month = parseInt(mm, 10) - 1;

	const day = parseInt(dd, 10);
	const year = parseInt(yyyy, 10);

	if (month < 0 || month > 11 || day < 1 || day > 31 || year < 1) return null;

	const maxDay = getDaysInMonth(year, month);
	if (day > maxDay) return null;

	return Date.UTC(year, month, day);
};
